'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { shippingAddressSchema } from '@payments-checkout/shared/helpers/constants/validation-input';
import { StringManager } from '@payments-checkout/shared/helpers/index';
import { reportError } from '@payments-checkout/shared/observability';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import {
  Alert,
  ButtonGroup,
  Icon,
  Spinner,
  Text,
  Tooltip,
} from '@stone-payments/jade';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  FormProvider,
  Resolver,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Replace } from '../../../app/components/replace/replace.compoment';
import { useFormTypedValues } from '../../../app/providers/formValuesProvider.provider';
import { ShippingAddressFormFields } from '../../../app/types/billingInformation.types';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useInstallmentsSettings } from '../../../hooks/useInstallmentsSettings/useInstallmentsSettings';
import { usePageSharedData } from '../../../hooks/usePageSharedData/usePageSharedData';
import { useSendShippingAddress } from '../../../hooks/useSendShippingAddress';
import { DeliveryQuotesResponse } from '../../../types/deliveryQuote.types';
import { InstallmentsResponse } from '../../../types/installments.types';
import { AddressFormComponent } from '../address/addressFormComponent';
import { AddressHeader } from '../address/addressHeader';

const DELIVERY_SCREEN = 'endereco_entrega';

const FIELD_REFERENCES: Record<string, string> = {
  'shipping-address-zip-code': 'campo_cep',
  'shipping-address-city': 'campo_cidade',
  'shipping-address-street': 'campo_endereco',
  'shipping-address-district': 'campo_bairro',
  'shipping-address-number': 'campo_numero',
  'shipping-address-complement': 'campo_complemento',
  'shipping-address-country': 'select_pais',
  'shipping-address-state': 'select_estado',
};

export function DeliveryForm({
  paymentLinkId,
}: {
  paymentLinkId: string;
}) {
  const { formatMessage } = useIntl();
  const { back, push } = useRouter();
  const { formValues, setFormValues, setDeliveryQuote, setInstallmentsStoneDelivery } = useFormTypedValues();
  const smallViewSize = useBreakpoint(500);
  const { hasInvalidPaymentSelected } = usePageSharedData();
  const [showDeliveryAlert, setShowDeliveryAlert] = useState(false);
  const [deliveryError, setDeliveryError] = useState('info.costs.delivery.error.quote');
  const { paymentMethod, setIsCustomerFormLoading, setInstallments, setInstallmentsExplanationText, setSurchargesText } = usePaymentMethod();

  const methods = useForm<ShippingAddressFormFields>({
    defaultValues: {
      'shipping-address-country':
        formValues?.['shipping-address-country'] ?? 'BR',
      'shipping-address-street': formValues?.['shipping-address-street'] ?? '',
      'shipping-address-number': formValues?.['shipping-address-number'] ?? '',
      'shipping-address-complement':
        formValues?.['shipping-address-complement'] ?? '',
      'shipping-address-district':
        formValues?.['shipping-address-district'] ?? '',
      'shipping-address-city': formValues?.['shipping-address-city'] ?? '',
      'shipping-address-state': formValues?.['shipping-address-state'] ?? '',
      'shipping-address-zip-code':
        formValues?.['shipping-address-zip-code'] ?? '',
      'shipping-address-without-number':
        formValues?.['shipping-address-without-number'] ?? false,
    },
    resolver: yupResolver(
      shippingAddressSchema
    ) as unknown as Resolver<ShippingAddressFormFields>,
  });

  const country = methods.watch('shipping-address-country');

  const { mutate: fetchInstallments, isPending: isFetchingInstallments } = useInstallmentsSettings({
    onSuccess: (response) => {
      const data = (response as { data: InstallmentsResponse })?.data;
      setInstallmentsStoneDelivery(data?.installments_plan ?? []);
      push(`/${paymentLinkId}/card-data`);
    },
    onError: (error) => {
      reportError(error, { source: 'delivery_installments' });
      setDeliveryError('errorGeneric.subtitle');
      setShowDeliveryAlert(true);
    },
  });

  const { mutate: sendShippingAddress, isPending } = useSendShippingAddress({
    onSuccess: async (response) => {
      const data = (response as { data: DeliveryQuotesResponse })?.data;
      const firstQuote = data?.quotes?.[0];
      if (firstQuote) {
        setDeliveryQuote(firstQuote);
        if (paymentMethod === 'credit_card') {
          fetchInstallments({ quoteId: firstQuote.id });
        } else {
          push(`/${paymentLinkId}/card-data`);
        }
      } else {
        push(`/${paymentLinkId}/card-data`);
      }
    },
    onError: (error) => {
      const { response } = error;
      reportError(error, { source: 'delivery_quote' });
      const detail = (response?.data as { detail?: string })?.detail;
      if (detail?.includes('noQuotesAvailable')) {
        analiticaInstance.events.addressEvents.deliveryAddressError();
        setDeliveryError('info.costs.delivery.error.address');
      } else {
        setDeliveryError('info.costs.delivery.error.quote');
      }
      setShowDeliveryAlert(true);
    },
  });

  useEffect(() => {
    analiticaInstance.events.addressEvents.deliveryAddressPageViewed();
    setIsCustomerFormLoading(false);
  }, []);

  const isLoading = isPending || isFetchingInstallments;

  const persistValues = () => {
    setFormValues((prev) => ({ ...prev, ...methods.getValues() }));
  };

  const onInvalid: SubmitErrorHandler<ShippingAddressFormFields> = (
    formErrors
  ) => {
    Object.entries(formErrors).forEach(([fieldName, error]) => {
      const reference = FIELD_REFERENCES[fieldName];
      if (!reference) return;

      analiticaInstance.events.addressEvents.addressFieldError(
        DELIVERY_SCREEN,
        reference,
        (error as { message?: string })?.message ?? ''
      );
    });
  };

  const onSubmit: SubmitHandler<ShippingAddressFormFields> = (data) => {
    analiticaInstance.events.addressEvents.deliveryAddressContinueClicked();
    persistValues();
    setShowDeliveryAlert(false);
    const zipCode = data['shipping-address-zip-code'];
    const country = data['shipping-address-country']?.toString() ?? 'BR';
    const address = {
      zip_code: StringManager.formatZipCode(zipCode ?? '', country),
      city: data['shipping-address-city'],
      state: data['shipping-address-state'],
      street: data['shipping-address-street'],
      neighborhood: data['shipping-address-district'],
      number:
        StringManager.filledString(data['shipping-address-number']) ?? 'S/N',
      complement: StringManager.filledString(
        data['shipping-address-complement'] ?? ''
      ),
      country,
      description: 'shipping-address',
    };
    sendShippingAddress({ address });
  };

  if (hasInvalidPaymentSelected) return <Replace />;
  return (
    <div className="w-full flex">
      <div className="flex flex-col grow px-jade-200 gap-jade-150 mx-auto mt-jade-200 mb-jade-300 max-w-[416px] md:mt-jade-600">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
            <AddressFormComponent
              formName="shipping-address"
              screen={DELIVERY_SCREEN}
              header={
                <div className="pb-jade-200">
                  <AddressHeader
                    formName="shipping-address"
                    country={country ?? 'BR'}
                    screen={DELIVERY_SCREEN}
                  />
                </div>
              }
            />
            {showDeliveryAlert && (
              <div className="mt-jade-300">
                <Alert
                  variant="warning"
                  showIcon
                  description={formatMessage({ id: deliveryError })}
                />
              </div>
            )}
            {isLoading && (
              <div className="flex justify-center items-center w-full gap-jade-150 mt-jade-300 mb-jade-300 flex-col">
                <Spinner
                  size="large"
                  color="brand"
                  data-testid="loading-delivery-request"
                />
              </div>
            )}
            <div className="w-full flex flex-col-reverse items-center justify-between mt-5 gap-jade-300 sm:flex-row">
              <div className="flex items-center gap-jade-100">
                <Icon color="medium" size="small" use="lock-close" />
                <Text
                  color="medium"
                  variant="text-medium"
                  weight="regular"
                  className="ml-jade-50 mr-jade-50 whitespace-nowrap"
                >
                  {formatMessage({ id: 'protectedPayment' })}
                </Text>
                <Tooltip
                  placement="top"
                  trigger={
                    <Icon color="medium" size="small" use="circle-info" />
                  }
                  text={formatMessage({ id: 'footer.tooltip' })}
                />
              </div>
              <div className="w-full md:w-2/5">
                <ButtonGroup
                  expanded={!!smallViewSize}
                  direction={smallViewSize ? 'vertical' : 'horizontal'}
                  items={[
                    {
                      id: 'delivery-page-continue-button',
                      text: formatMessage({ id: 'button.Continue' }),
                      variant: 'primary-solid',
                      type: 'submit',
                      loading: isLoading,
                    },
                    {
                      text: formatMessage({ id: 'button.action.back' }),
                      onClick: () => {
                        analiticaInstance.events.addressEvents.deliveryAddressBackClicked();
                        persistValues();
                        setInstallments(null);
                        setInstallmentsExplanationText(null);
                        setSurchargesText(null);
                        back();
                      },
                    },
                  ]}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
